import React, {useState} from 'react';

import {Anchor, Banner, Button, Icon, Text} from '@albert-io/atomic';
import EmptyState from 'client/Classrooms/Classroom/Teacher/EmptyState/index.react';
import MegaClassesEmptyState from 'client/Classrooms/Classroom/Teacher/MegaClassesEmptyState/MegaClassesEmptyState.react';
import LoadingIndicator from 'generic/LoadingIndicator.react';

import {ClassOverviewScopeBar} from './ClassOverviewScopeBar';
import {MasteryColumns} from './MasteryColumns';
import {OverviewSortMenu} from './OverviewSortMenu';
import type {OverviewSortKey} from './OverviewSortMenu';
import {StatTiles} from './StatTiles';
import {useClassOverview} from './useClassOverview';

interface Props {
  classroomId: string;
}

// v2 Overview tab: class-level stat tiles + a mastery-bucketed student roster, scoped by
// subject/attempt/date. Flux-free port of the legacy Teacher/TeacherDashboard overview.
export const Overview = ({classroomId}: Props) => {
  const overview = useClassOverview(classroomId);
  const [sortBy, setSortBy] = useState<OverviewSortKey>('name');

  if (overview.hasError) {
    return (
      <div className='pt-x4'>
        <Banner type='negative' className='flex justify-center'>
          <Banner.Icon icon='exclamation' />
          <div className='flex flex-col items-center gap-x1'>
            <span>We couldn’t load this class’s overview.</span>
            <Button variant='outlined' color='secondary' onClick={overview.reload}>
              Try again
            </Button>
          </div>
        </Banner>
      </div>
    );
  }

  if (!overview.classroomReady) {
    return <LoadingIndicator />;
  }

  if (overview.isMegaClass) {
    return <MegaClassesEmptyState supportingTitleText='Overview data will not load' />;
  }

  if (overview.hasNoSubjects) {
    return (
      <EmptyState
        title='No overview data'
        body='Classroom data will appear when students answer questions. Visit the library to assign questions.'
      />
    );
  }

  return (
    <div className='flex flex-col gap-x4 pt-x4'>
      <ClassOverviewScopeBar overview={overview} />
      {overview.isLoadingStudents ? (
        <div className='flex justify-center pt-x4'>
          <LoadingIndicator />
        </div>
      ) : (
        <>
          <StatTiles tiles={overview.tiles} />
          <hr className='m-x0 border-0 border-t border-border-primary' />
          <div className='flex flex-col'>
            <div className='flex items-center justify-between'>
              <Text as='h2' bold size='l'>
                Accuracy
              </Text>
              <div className='flex items-center font-semibold content-link'>
                <span aria-hidden>Sort by</span>
                <OverviewSortMenu sortBy={sortBy} onChange={setSortBy} />
              </div>
            </div>
            <Text size='s' color='secondary' className='mt-x1'>
              Accuracy excludes partial credit, for a more detailed breakdown, check out{' '}
              <Anchor to={`/reports/classrooms/${classroomId}/students`}>
                reports <Icon icon='arrow-up-right-from-square' iconStyle='regular' />
              </Anchor>
            </Text>
            <hr className='mt-x1 border-0 border-t border-border-primary' />
            {overview.students && (
              <div className='mt-x4'>
                <MasteryColumns
                  students={overview.students}
                  classroomId={classroomId}
                  sortBy={sortBy}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
